import React from 'react';
import { Typography, Paper } from '@mui/material';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';

interface CohortGuardProps {
  /** The active user count for the current filter context. */
  userCount: number | null | undefined;
  /** Minimum number of participants required before showing data. Defaults to 2. */
  minCohort?: number;
  /**
   * When true, children are rendered as-is regardless of userCount.
   * Use only when the selected scope does not require anonymisation.
   */
  bypass?: boolean;
  /**
   * When true, withhold children until the cohort count is known.
   */
  loading?: boolean;
  children: React.ReactNode;
}

/** Withhold chart components entirely until the current cohort passes privacy checks. */
export const CohortGuard: React.FC<CohortGuardProps> = ({
  userCount,
  minCohort = 2,
  bypass = false,
  loading = false,
  children,
}) => {
  if (bypass) return <>{children}</>;
  if (!loading && typeof userCount === 'number' && userCount >= minCohort) {
    return <>{children}</>;
  }

  // Suppressed charts must not be mounted: CSS blur leaves data in the DOM.
  return (
    <Paper role='status' elevation={0} sx={{ p: 3, textAlign: 'center', width: '100%' }}>
      <PeopleOutlineIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
      <Typography variant='subtitle1' fontWeight={600}>
        {loading ? 'Checking cohort size' : 'Insufficient cohort size'}
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        {loading
          ? 'Data will appear once the privacy check is complete.'
          : `To protect participants’ privacy, data is hidden when fewer than ${minCohort} participants have data. Broaden your filters or date range.`}
      </Typography>
    </Paper>
  );
};
